import { getCancelledFileUploadSessions, getFileUploadSessionById, type FileUploadSession } from "@/db/fileUploadDb";
import { getAdminDurableOwnershipForCoordinator } from "./adminUploadLease";
import { cancelAdminUploadSession } from "./adminUploadCancellation";
import { uploadFailureLogDetails } from "./uploadLogRedaction";
import { newUploadId, type DurableLeaseToken, type DurableOwnershipPort } from "@uploads";

export const ADMIN_CANCELLED_UPLOAD_DRAIN_LIMIT = 32;
const ADMIN_CANCELLED_UPLOAD_WRITER_TTL_MS = 2 * 60 * 1000;

export interface AdminCancelledUploadDrainOptions {
	limit?: number;
	ownerId?: string;
	shouldContinue?: () => boolean;
	ownership?: DurableOwnershipPort;
}

export interface AdminCancelledUploadDrainResult {
	observed: number;
	acknowledged: number;
	pending: number;
	skipped: number;
}

function sessionOwner(session: FileUploadSession) {
	return { uploadOwnerId: session.uploadOwnerId, uploadOwnerClaimId: session.uploadOwnerClaimId };
}

async function assertWriterEpoch(ownership: DurableOwnershipPort, token: DurableLeaseToken, shouldContinue: () => boolean) {
	return shouldContinue() && (await ownership.ownsWriter(token));
}

/**
 * Reconciles durable cancellation tombstones left by a prior page/tab. This is deliberately
 * separate from the pending resume queue: cancelled rows retain their local bytes until the
 * server abort/revocation is acknowledged, and a bounded per-session writer epoch prevents two
 * tabs from issuing duplicate aborts or deleting a successor's row.
 */
export async function drainCancelledAdminUploadSessions(
	options: AdminCancelledUploadDrainOptions = {}
): Promise<AdminCancelledUploadDrainResult> {
	const shouldContinue = options.shouldContinue ?? (() => true);
	const limit = options.limit ?? ADMIN_CANCELLED_UPLOAD_DRAIN_LIMIT;
	// Legacy multipart tombstones remain owned by fileUploadDb.drainCancelledUploadSessions. This
	// lane only handles rows with the stable /uploads identity, so startup can run both drains without
	// issuing duplicate aborts for the same legacy MPU.
	const cancelled = await getCancelledFileUploadSessions(
		limit,
		(session) => session.endpointFamily === "uploads" || session.registryId !== undefined
	);
	const result: AdminCancelledUploadDrainResult = { observed: cancelled.length, acknowledged: 0, pending: 0, skipped: 0 };
	if (cancelled.length === 0 || !shouldContinue()) return result;

	let ownership = options.ownership;
	if (!ownership) {
		try {
			ownership = await getAdminDurableOwnershipForCoordinator();
		} catch (error) {
			console.warn("[Upload] Durable ownership unavailable; cancelled-upload drain deferred", uploadFailureLogDetails(error));
			result.pending = cancelled.length;
			return result;
		}
	}
	if (!ownership) {
		result.pending = cancelled.length;
		return result;
	}

	for (const snapshot of cancelled) {
		if (!shouldContinue()) break;
		let token: DurableLeaseToken | undefined;
		try {
			token = await ownership.acquireWriter({
				originKey: "contact-admin",
				identity: { itemId: snapshot.id },
				ownerId: options.ownerId ?? `admin-cancel-drain:${newUploadId()}`,
				ttlMs: ADMIN_CANCELLED_UPLOAD_WRITER_TTL_MS
			});
			if (!token) {
				result.skipped++;
				continue;
			}

			const current = await getFileUploadSessionById(snapshot.id);
			if (!current || current.status !== "cancelled" || current.uploadId !== snapshot.uploadId) {
				result.skipped++;
				continue;
			}
			const cancelResult = await cancelAdminUploadSession({
				sessionId: current.id,
				expectedUploadId: current.uploadId,
				expectedOwner: sessionOwner(current),
				expectedStatus: "cancelled",
				assertWriterEpoch: () => assertWriterEpoch(ownership!, token!, shouldContinue),
				writer: { ownership, token }
			});
			if (cancelResult === "acknowledged" || cancelResult === "completed" || cancelResult === "missing") result.acknowledged++;
			else result.pending++;
		} catch (error) {
			result.pending++;
			console.warn(`[Upload] Cancelled-upload drain failed for session ${snapshot.id} — will retry`, uploadFailureLogDetails(error));
		} finally {
			if (token) {
				await ownership
					.releaseWriter(token)
					.catch((error) =>
						console.warn(
							`[Upload] Could not release cancelled-upload writer for session ${snapshot.id}`,
							uploadFailureLogDetails(error)
						)
					);
			}
		}
	}
	return result;
}
